import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { DeliverableItemStatus } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, Caption } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { DumbCardBack } from '@sorare/core/src/components/card/Back/DumbCardBack';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { RainbowCircle } from '@sorare/core/src/components/rewards/Banner/RainbowCircle';
import { useGenerateDialogPath } from '@sorare/core/src/hooks/navigation/useGenerateDialogPath';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { isType } from '@sorare/core/src/lib/gql';

import jerseyBack from '@sorare/gameplay/src/components/rewards/deliverableItem/DeliverableItemFooter/jersey/assets/jersey_back.png';
import experienceBack from '@sorare/gameplay/src/components/rewards/deliverableItem/ExperienceRewardCard/experience_back.png';
import ticketsBack from '@sorare/gameplay/src/components/rewards/deliverableItem/TicketsRewardCard/tickets_back.png';

import { UnclaimedDeliverableItemOrderButton_deliverableItemOrder } from './__generated__/index.graphql';

const StyledCardBack = styled(DumbCardBack)`
  width: 20px;
`;

const RainbowWrapper = styled.div`
  grid-area: icon;
  width: calc(8 * var(--unit));
`;

const Content = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-start;
  text-align: left;
`;

type UnclaimedDeliverableItemOrderButtonProps = {
  deliverableItemOrder: UnclaimedDeliverableItemOrderButton_deliverableItemOrder;
};
const UnclaimedDeliverableItemOrderButton = ({
  deliverableItemOrder,
}: UnclaimedDeliverableItemOrderButtonProps) => {
  const generateDialogPath = useGenerateDialogPath();
  const { message, isEnded } = useTimeLeft(deliverableItemOrder.expiresAt);
  const showItem =
    deliverableItemOrder.aasmState === DeliverableItemStatus.READY;
  if (!showItem || isEnded) return null;

  let cardBack = jerseyBack;
  if (
    isType(deliverableItemOrder.deliverableItem, 'ExperienceDeliverableItem')
  ) {
    cardBack = experienceBack;
  }
  if (isType(deliverableItemOrder.deliverableItem, 'TicketsDeliverableItem')) {
    cardBack = ticketsBack;
  }

  return (
    <Link
      to={generateDialogPath(
        DialogKey.deliveryItemOrder,
        deliverableItemOrder.id
      )}
    >
      <ButtonBase color="tertiary">
        <Horizontal>
          <RainbowWrapper>
            <RainbowCircle>
              <StyledCardBack path={cardBack} aria-label="Jersey" />
            </RainbowCircle>
          </RainbowWrapper>
          <Content>
            <div>
              <BodyS as="p">
                <FormattedMessage
                  id="UnclaimedDeliverableItemOrderButton.item.title"
                  defaultMessage="One item to order"
                />
              </BodyS>
              {deliverableItemOrder.expiresAt && (
                <Caption color="var(--c-neutral-700)">
                  <FormattedMessage
                    id="UnclaimedDeliverableItemOrderButton.time"
                    defaultMessage="{time} left to order"
                    values={{ time: message }}
                  />
                </Caption>
              )}
            </div>
            <Chip>
              <Caption bold>
                <FormattedMessage
                  id="UnclaimedDeliverableItemOrderButton.cta"
                  defaultMessage="Order now"
                />
              </Caption>
            </Chip>
          </Content>
        </Horizontal>
      </ButtonBase>
    </Link>
  );
};

UnclaimedDeliverableItemOrderButton.fragments = {
  deliverableItemOrder: gql`
    fragment UnclaimedDeliverableItemOrderButton_deliverableItemOrder on DeliverableItemOrder {
      id
      aasmState
      expiresAt
      deliverableItem {
        slug
        id
      }
    }
  ` as TypedDocumentNode<UnclaimedDeliverableItemOrderButton_deliverableItemOrder>,
};

export default UnclaimedDeliverableItemOrderButton;
